import React, { useEffect } from "react";

import Above from "core-web/components/Breakpoints/Above";
import Below from "core-web/components/Breakpoints/Below";
import ChevronIcon from "../icons/ChevronIcon";
import Link from "core-web/components/Link";
import TextLink from "theme/components/text/TextLink";
import TextStyles from "theme/components/text/TextStyles";
import ThemeButton from "../buttons/ThemeButton";
import colors from "theme/config/colors";
import showOverlay from "core-web/state/models/Overlay/dispatches/showOverlay";
import styled from "theme/libs/styled";
import useIntersectionObserver from "core-web/hooks/useIntersectionObserver";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const NavToggleWrapper = styled("div")`
    position: fixed;
    inset: auto 20px 8px;
    z-index: 1;
    transition: transform ${({ theme }) => theme.transitions.primary};

    &[data-visibly-hidden="true"] {
        transform: translateY(calc(100% + 8px));
    }
`;

const DesktopNav = styled("nav")`
    position: sticky;
    top: calc(${({ theme }) => theme.variables.headerHeight}px + 12px);
`;

const StyledList = styled("ul")`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 48px 24px 0 0;
`;

const CustomerServiceMenu = () => {
    const { t } = useTranslation();
    const title = useSelector(({ page }) => page.data?.title) || t("views.customer_service.overview_title");
    const permalink = useSelector(({ page }) => page?.data?.permalink);
    const childPages = useSelector(({ page }) => page?.data?.children || []);
    const siblingPages = useSelector(({ page }) => page?.data?.siblings || []);
    const parentPage = useSelector(({ page }) => page?.data?.parent || {});
    const currentPage = { title, permalink };
    const topLevelPage = siblingPages.length === 0 ? currentPage : parentPage;
    const pages = [...siblingPages, ...childPages];

    const openOverlay = () => {
        showOverlay("content_page_nav_overlay");
    };

    const [footerRef, setFooterRef] = useIntersectionObserver();

    useEffect(() => {
        setFooterRef(document.getElementById("site-footer"));
    }, []);

    /*
     * TODO: Design wants us to show even "grandchildren" in the CustomerServiceMenu:
     * For example on the page "/customer-service" the design wants us to show
     * menu items for child pages like "/customer-service/about-us" and "/customer-service/contact-us" but
     * even grandchildren like "/customer-service/about-us/team" and "/customer-service/about-us/open-positions".
     * Currently we can only access a page's siblings and children but not grandchildren.
     * Adding access to grandchildren could be a part of "V2".
     */

    return (
        <>
            <Below
                breakpoint="tabletLg"
                render={() => (
                    <NavToggleWrapper data-visibly-hidden={footerRef?.isIntersecting}>
                        <ThemeButton
                            buttonTheme="white_solid"
                            size="xs"
                            width="min(385px, 100%)"
                            m="auto"
                            boxShadow="0px 4px 36px 0px rgba(0, 0, 0, 0.08)"
                            aria-label={t("aria_labels.open_nav")}
                            onClick={openOverlay}
                        >
                            {topLevelPage?.title} / {currentPage.title}
                            <ChevronIcon direction="down" />
                        </ThemeButton>
                    </NavToggleWrapper>
                )}
            />

            <Above
                breakpoint="tabletLg"
                render={() => (
                    <DesktopNav>
                        <StyledList>
                            <li>
                                <Link to={topLevelPage?.permalink}>
                                    <TextStyles
                                        textStyles="Myriad Pro/24-100-semibold"
                                        borderBottom={`1px solid ${colors.black_10}`}
                                        pb="24px"
                                        mb="18px"
                                    >
                                        {topLevelPage?.title}
                                    </TextStyles>
                                </Link>
                            </li>
                            {(pages || []).map((page) => (
                                <li key={`${page.title}-${page.permalink}`}>
                                    <TextLink
                                        to={page.permalink || ""}
                                        display="inline-block"
                                        isActive={page.permalink === permalink}
                                    >
                                        <TextStyles as="span" textStyles="Myriad Pro/14-100">
                                            {page.title}
                                        </TextStyles>
                                    </TextLink>
                                </li>
                            ))}
                        </StyledList>
                    </DesktopNav>
                )}
            />
        </>
    );
};

export default CustomerServiceMenu;
